<template>
  <div class="map-view-grid-item-container">
    <div :style="maskingCssStyle"></div>
    <div :id="mapId" :style="mapCssStyle" class="vs-con-loading__container">
      <plot-layer
        :is-ready="isStyleReady"
        :map="map"
        :gridMapSetting="gridMapSetting"
      />
      <div v-if="getAddonFlag('GKMG_PRIVATE_PLOT')">
        <private-plot-layer
          :is-ready="isStyleReady"
          :map="map"
          :gridMapSetting="gridMapSetting"
          :filterPrivatePlots="filterPrivatePlots"
        />
      </div>
      <vessel-map-layer
        :is-ready="isStyleReady"
        :map="map"
        :mapEvent="mapEvent"
        :mapId="mapId"
        :gridMapSetting="gridMapSetting"
      />
      <traffic-routes-layer
        :is-ready="isStyleReady"
        :map="map"
        :mapEvent="mapEvent"
        :mapId="mapId"
        :gridMapSetting="gridMapSetting"
      />
      <navigational-aids-layer
        :is-ready="isStyleReady"
        :map="map"
        :mapEvent="mapEvent"
        :mapId="mapId"
        :gridMapSetting="gridMapSetting"
      />
      <vehicle-layer :is-ready="isStyleReady" :map="map" :mapEvent="mapEvent" />
      <div v-if="getAddonFlag('GKMG_ANCHOR_MANAGEMENT')">
        <vessel-anchor-points-layer
          :is-ready="isStyleReady"
          :map="map"
          :mapEvent="mapEvent"
        />
      </div>
      <div v-if="getAddonFlag('GKMG_BASE_STATION')">
        <base-station-layer
          :is-ready="isStyleReady"
          :map="map"
          :mapEvent="mapEvent"
        />
      </div>
      <div v-if="getAddonFlag('GKMG_RADAR_GRID')">
        <radar-grid-layer
          :is-ready="isStyleReady"
          :map="map"
          :mapEvent="mapEvent"
        />
      </div>
      <div id="map-loading" class="vs-con-loading__container"></div>
      <map-menu :mapId="mapId" />
      <default-position-button :mapId="mapId" :backToDefault="backToDefault" />
      <div v-if="isDebug">
        <debug-card :debugObject="debugObject" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * ４画面マップ中の１マップを表示するコンポーネント
 * @parent O.MapViewGridItem
 * @props mapId: String １マップに割り当てられているマップid
 * @props top: String マップを分割するCSSに使用する
 * @props left: String マップを分割するCSSに使用する
 */
import Vue from "vue";
import { createNamespacedHelpers } from "vuex";
import { mapGetters } from "vuex";
import FireStoreService from "@/services/fireStoreService";
import Mapbox from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import LocalDbService from "@/services/localDbService";

// plot周り
import PlotLayer from "@/components/organisms/PlotLayer";
import PrivatePlotLayer from "@/components/organisms/PrivatePlotLayer";
import VesselMapLayer from "@/components/organisms/VesselMapLayer";
import TrafficRoutesLayer from "@/components/organisms/TrafficRoutesLayer";
import NavigationalAidsLayer from "@/components/organisms/NavigationalAidsLayer";
import VehicleLayer from "@/components/organisms/VehicleLayer";
import VesselAnchorPointsLayer from "@/components/organisms/VesselAnchorPointsLayer";
import BaseStationLayer from "@/components/organisms/BaseStationLayer";
import RadarGridLayer from "@/components/organisms/RadarGridLayer";

import _ from "lodash";
import * as turf from "@turf/turf";
import moment from "moment-timezone";
import AuthService from "@/services/authService";
import { MAP_DEFAULT_POSITION, MAP_STYLE_DEFAULT } from "@/config/const";

import TileBelt from "@mapbox/tilebelt";
import QuadKeyTools from "quadkeytools";
import {
  MapSource,
  PlotMapSource,
  PrivatePlotMapSource,
} from "@/config/Map.js";
import WebWorkerForFetchVesselGeometry from "worker-loader!@/utils/worker/WebWorkerForFetchVesselGeometry.js";
import WebWorkerForFetchVesselGeometryForSignal from "worker-loader!@/utils/worker/WebWorkerForFetchVesselGeometryForSignal.js";
import MapMenu from "@/components/organisms/MapMenu";
import DefaultPositionButton from "@/components/atoms/DefaultPositionButton";
import DebugCard from "@/components/molecules/DebugCard";

// Store
const {
  mapActions: mapActionsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
} = createNamespacedHelpers("liveMap");

const {
  mapActions: mapActionsOfGridMap,
  mapState: mapStateOfGridMap,
  mapMutations: mapMutationsOfGridMap,
} = createNamespacedHelpers("gridMap");

const { mapActions: mapActionsOfPrivatePlot, mapState: mapStateOfPrivatePlot } =
  createNamespacedHelpers("gkmgPrivatePlot");

const {
  mapActions: mapActionsOfAnchor,
  mapState: mapStateOfAnchor,
  mapGetters: mapGettersOfAnchor,
} = createNamespacedHelpers("gkmgAnchor");

const {
  mapActions: mapActionsOfVesselEquipmentBaseStation,
  mapState: mapStateOfVesselEquipmentBaseStation,
} = createNamespacedHelpers("gkmgVesselEquipmentBaseStation");

// Mixins
import {
  LocalizeMixin,
  DeviceInfoMixin,
  LayerMixin,
  ColorMixin,
} from "@/mixins";

export default {
  name: "O.MapViewGridItem",
  components: {
    MapMenu,
    DefaultPositionButton,
    PlotLayer,
    PrivatePlotLayer,
    VesselMapLayer,
    TrafficRoutesLayer,
    NavigationalAidsLayer,
    VesselAnchorPointsLayer,
    BaseStationLayer,
    DebugCard,
    VehicleLayer,
    RadarGridLayer,
  },
  mixins: [LocalizeMixin, DeviceInfoMixin, LayerMixin, ColorMixin],
  props: {
    mapId: {
      type: String,
      required: true,
    },
    top: {
      type: String,
      required: true,
    },
    left: {
      type: String,
      required: true,
    },
    vesselPositions: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      /** アクセストークン（map取得に必要） */
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      /** 表示するマップのスタイル */
      mapStyleLocal: null,
      /** マップのインスタンス */
      map: null,
      mapEvent: null,
      isStyleReady: false,

      // Map life cycle
      // updateVesselPositionsIntervalId: null, // TODO: 現状不要なのでコメントアウト
      searchQuadKeys: [],

      // キャッシュ
      // timerMapMove: null,
      cache: {
        quadKey: null, // 同じQuadKeyエリアは表示しない用
        searchQuadKeys: [], // 検索QuadKey一覧
      },

      // Tab
      tabIndex: 0,
      // storeUnsubscribe: null,
      // doMapResize: false,
      // circleRadius: 400,

      webWorker: null,
      intervalIdForGetFeatureCollection: [],
      intervalHistoryList: [],
      intervalIdForGetFeatureCollectionLong: [],
      timeoutIdForGetFeatureCollection: [],
      isReadyGetFeatureCollectionLong: false,
      featurePositionMap: new Map(),
      featureVectorMap: new Map(),
      featurePolygonMap: new Map(),

      navigationalAidsSourceId: "navigationalAidsSource",
      // グリッドマップ内に表示するポップアップメッセージ
      expiredPopup: null,
      timerId: null,
      // デバッグ表示
      isDebug: false,
      // 追跡設定中船舶
      trackingTargetVessel: { vesselPosition: null },
    };
  },
  computed: {
    ...mapStateOfLiveMap({
      ownVessels: (state) => state.ownVessels,
      selectedVessels: (state) => state.monitoringVessels,
      ownVesselPointFeatureCollection: (state) =>
        state.ownVesselPointFeatureCollection,
      ownVesselPolygonFeatureCollection: (state) =>
        state.ownVesselPolygonFeatureCollection,
      selectedVesselPointFeatureCollection: (state) =>
        state.selectedVesselPointFeatureCollection,
      selectedVesselPolygonFeatureCollection: (state) =>
        state.selectedVesselPolygonFeatureCollection,
      isNewSelectedVessel: (state) => state.isNewSelectedVessel,
      ownVesselDisplayDurationTime: (state) =>
        state.ownVesselDisplayDurationTime,
    }),
    ...mapStateOfGridMap({
      allGridMapSettings: (state) => state.allGridMapSettings,
      lastTrackingPosition: (state) => state.lastTrackingPosition,
      // ポップアップメッセージの表示管理
      expiredPopupForGrid: (state) => state.expiredPopupForGrid,
      // 右メニューの状態管理
      activeSidebarForGrid: (state) => state.activeSidebarForGrid,
    }),
    ...mapStateOfPrivatePlot({
      privatePlots: (state) => state.privatePlots,
    }),
    ...mapStateOfAnchor({
      anchors: (state) => state.anchors,
    }),
    ...mapStateOfVesselEquipmentBaseStation({
      equipmentBaseStations: (state) => state.equipmentBaseStations,
    }),
    ...mapGettersOfLiveMap(["getLastVesselPosition"]),
    ...mapGettersOfAnchor(["getAnchorVesselIds"]),
    ...mapGetters({
      privatePlotFeaturesForPoint:
        "gkmgPrivatePlot/getPrivatePlotFeaturesForPoint",
      privatePlotFeaturesForPolygon:
        "gkmgPrivatePlot/getPrivatePlotFeaturesForPolygon",
      getAddonFlag: "getAddonFlag",
    }),
    isNoMapSetting() {
      return !this.activeMenuMapId;
    },
    isMapSetting() {
      return this.activeMenuMapId === this.mapId;
    },
    isOtherMapSetting() {
      return !this.isNoMapSetting && !this.isMapSetting;
    },
    /**
     * マップのCSSスタイル設定
     * （１）自身のメニューが開いている
     * （２）他マップのメニューが開いている
     * （３）すべてのメニューが閉じている
     */
    mapCssStyle() {
      // （１）自身のメニューが開いている
      if (this.isMapSetting) {
        return {
          "min-width": "calc(50vw - 4px)",
          "min-height": "calc(50vh - 4px)",
          position: "absolute",
          top: `calc(${this.top} + 2px)`,
          left: `calc(${this.left} + 2px)`,

          outline: "4px solid #2D9CDB",
        };
      }
      // （２）他マップのメニューが開いている
      if (this.isOtherMapSetting) {
        return {
          "min-width": "calc(50vw - 4px)",
          "min-height": "calc(50vh - 4px)",
          top: `calc(${this.top} + 2px)`,
          left: `calc(${this.left} + 2px)`,
          position: "absolute",
          outline: "2px solid #F8F8F8",
          "pointer-events": "none",
        };
      }
      // （３）すべてのメニューが閉じている
      return {
        "min-width": "calc(50vw - 4px)",
        "min-height": "calc(50vh - 4px)",
        position: "absolute",
        top: `calc(${this.top} + 2px)`,
        left: `calc(${this.left} + 2px)`,

        outline: "2px solid #F8F8F8",
      };
    },
    /**
     * 他マップのメニューを開いているときにマスクをかける
     */
    maskingCssStyle() {
      if (this.isOtherMapSetting) {
        return {
          "min-width": "calc(50vw)",
          "min-height": "calc(50vh)",

          position: "absolute",
          top: `calc(${this.top})`,
          left: `calc(${this.left})`,
          // マスク部分
          "background-color": "#00000066",
          "z-index": "10",
        };
      }
      return {};
    },
    activeMenuMapId() {
      return this.activeSidebarForGrid.mapId;
    },
    gridMapSetting() {
      return (
        this.allGridMapSettings.find((gms) => gms.map_id === this.mapId) || {}
      );
    },
    mapStyle() {
      if (!this.gridMapSetting) return MAP_STYLE_DEFAULT;
      return this.gridMapSetting.map_style || MAP_STYLE_DEFAULT;
    },
    defaultPosition() {
      if (!this.gridMapSetting) return MAP_DEFAULT_POSITION;
      return this.gridMapSetting.default_position || MAP_DEFAULT_POSITION;
    },
    /** isDebugがtrueのときに表示する内容 */
    debugObject() {
      return {
        lng: this.map ? this.map.getCenter().lng : "--",
        lat: this.map ? this.map.getCenter().lat : "--",
        zoom: this.map ? this.map.getZoom() : "--",
      };
    },
    filterPrivatePlots() {
      if (!this.gridMapSetting) return [];
      return this.gridMapSetting.filter_private_plots || [];
    },
  },
  async mounted() {
    await FireStoreService.initGridMapSetting(this.mapId);
    const currentSettings = await FireStoreService.getGridMapSettings(
      this.mapId,
    );
    this.UPDATE_GRID_MAP_SETTINGS(currentSettings);
    // 読込情報を取得
    const pos = {
      center: currentSettings.tracking_target
        ? await this.setTrackingTargetCenter(currentSettings)
        : this.defaultPosition.center,
      zoom: currentSettings.last_zoom
        ? currentSettings.last_zoom
        : this.defaultPosition.zoom,
    };
    this.mapStyleLocal = currentSettings.map_style;
    // アクセストークン
    Mapbox.accessToken = this.accessToken;
    // インスタンス生成
    let map = new Mapbox.Map({
      container: this.mapId,
      style: this.mapStyleLocal,
      center: pos.center,
      zoom: pos.zoom,
      doubleClickZoom: false,
      maxZoom: 18,
      attributionControl: false,
      localIdeographFontFamily: "'Noto Sans', sans-serif",
    });
    // スケールの初期化
    const scale = new Mapbox.ScaleControl({
      maxWidth: 80,
      unit: "metric",
    });
    map.addControl(scale);
    // 左下のナビゲーション
    let nav = new Mapbox.NavigationControl();
    map.addControl(nav, "bottom-left");
    // canvasのサイズ変更を反映する
    map.resize();

    this.map = map; // ここで紐付けないと setSubscribes() 時にエラー発生
    // map.on("load", this.mapLoaded);
    map.on("movestart", this.mapMoveStart);
    map.on("moveend", this.mapMoveEnd);
    map.on("zoomstart", this.mapZoomStart);
    map.on("zoomend", this.mapZoomEnd);
    map.on("pitch", this.mapPitch);
    map.on("style.load", this.mapStyleLoad);
  },
  watch: {
    async lastTrackingPosition(newLayer) {
      if (!newLayer.mapId) return;
      if (newLayer.mapId !== this.mapId) return;
      this.removeExpiredPopup();
      if (newLayer.center && newLayer.center.length) {
        this.map.jumpTo({ center: newLayer.center });
      } else {
        this.backToDefault();
      }
    },
    // ポップアップメッセージを表示
    expiredPopupForGrid(newValue) {
      if (newValue.mapId === this.mapId) {
        if (!newValue.popupObject) return;
        this.removeExpiredPopup();
        this.expiredPopup = new Mapbox.Popup(newValue.popupObject)
          .setLngLat(newValue.center)
          .setHTML(newValue.text);
        setTimeout(() => {
          this.expiredPopup.addTo(this.map);
        }, 100);
        this.timerId = setTimeout(() => {
          this.expiredPopup.remove();
          this.expiredPopup = null;
        }, 60000); // 1分経過後に自動で消える
      }
    },
    selectedVessels() {
      if (this.selectedVessels.length > 0) {
        this.tabIndex = 1;
      }
    },
    privatePlots() {
      this.createPrivatePlotFeatures(this.privatePlots);
    },
    /** プライベートプロット(ポイント) */
    privatePlotFeaturesForPoint() {
      if (!this.getAddonFlag("GKMG_PRIVATE_PLOT")) return;
      if (!this.map) return;
      const privatePlotPointSource = this.map.getSource(
        PrivatePlotMapSource.PrivatePlotPointSource.id,
      );
      if (!privatePlotPointSource) return;
      privatePlotPointSource.setData({
        type: "FeatureCollection",
        features: this.privatePlotFeaturesForPoint,
      });
    },
    /** プライベートプロット(ポリゴン) */
    privatePlotFeaturesForPolygon() {
      if (!this.getAddonFlag("GKMG_PRIVATE_PLOT")) return;
      if (!this.map) return;
      const privatePlotPolygonSource = this.map.getSource(
        PrivatePlotMapSource.PrivatePlotPolygonSource.id,
      );
      if (!privatePlotPolygonSource) return;
      privatePlotPolygonSource.setData({
        type: "FeatureCollection",
        features: this.privatePlotFeaturesForPolygon,
      });
    },
    mapStyle(newVal, oldVal) {
      if (newVal === oldVal) return;
      if (!this.isStyleReady) return;
      this.$nextTick(() => {
        if (this.map) {
          this.isStyleReady = false;
          this.map.setStyle(newVal);
          this.mapStyleLocal = newVal;
        }
      });
    },
    // 追跡設定が変更になった場合の監視
    gridMapSetting() {
      this.setTrackingTargetVessel();
    },
    // 起動時に設定中のvesselPositionを格納するための監視
    vesselPositions() {
      this.setTrackingTargetVessel();
    },
    // 追跡中の船舶座標が変わったらマップの中心座標を移動する
    "trackingTargetVessel.vesselPosition.l": function (newValue) {
      if (!this.map) return;
      if (newValue && newValue.longitude != null && newValue.latitude != null) {
        this.map.jumpTo({ center: [newValue.longitude, newValue.latitude] });
      }
    },
  },
  methods: {
    ...mapActionsOfLiveMap(["resetLiveMapState"]),
    ...mapActionsOfGridMap(["getLastPositionWithTimeout"]),
    ...mapMutationsOfGridMap([
      "UPDATE_GRID_MAP_SETTINGS",
      "UPDATE_LAST_TRACKING_POSITION",
    ]),
    ...mapActionsOfPrivatePlot([
      "fetchPrivatePlots",
      "createPrivatePlotFeatures",
      "resetPrivatePlotState",
    ]),
    ...mapActionsOfAnchor(["resetAnchorState"]),
    ...mapActionsOfVesselEquipmentBaseStation([
      "resetVesselEquipmentBaseStationState",
    ]),
    /** tracking_targetを解除してデフォルトポジションに戻る */
    async backToDefault() {
      this.removeExpiredPopup();
      this.map.jumpTo(this.defaultPosition);
      // store更新
      this.UPDATE_GRID_MAP_SETTINGS({
        map_id: this.mapId,
        tracking_target: {},
      });
      await FireStoreService.setGridMapSettings(this.mapId, {
        tracking_target: {},
        last_zoom: null,
      });
    },
    /** 追跡設定中の船舶のポジションを格納する */
    setTrackingTargetVessel() {
      if (!this.gridMapSetting || !this.gridMapSetting.tracking_target) return;
      if (this.gridMapSetting.tracking_target.type !== "vessel") {
        this.trackingTargetVessel = { vesselPosition: null };
      } else {
        this.trackingTargetVessel = this.vesselPositions.find((vessel) => {
          return vessel.vesselId === this.gridMapSetting.tracking_target.id;
        });
      }
    },
    // 不要なポップアップメッセージを削除
    removeExpiredPopup() {
      if (this.expiredPopup !== null) {
        this.expiredPopup.remove();
        this.expiredPopup = null;
      }
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    },
    // TODO: 現状何も行わないためコメントアウト
    /** マップレンダリング後実行する */
    // mapLoaded() {
    // this.setSubscribes();
    // },
    /** マップのスタイルレンダリング後実行する */
    async mapStyleLoad(_a, _b) {
      if (this.isDefaultMapStyle(this.mapStyleLocal)) {
        let lang = this.getBrowserLanguage();
        let local = "name_" + lang;
        if (this.map) {
          let textLayerIds = this.getTextLayerIds();
          textLayerIds.forEach((id) => {
            this.map.setLayoutProperty(id, "text-field", ["get", local]);
          });
        }
      }

      await this.setupMapSources();

      this.isStyleReady = true;
      this.cache.quadKey = null;

      this.cratePulsing(this.map);
      this.addAnimationIcon();

      this.updateMap(this);

      if (this.getAddonFlag("GKMG_PRIVATE_PLOT")) {
        // 初回起動時だけプライベートプロットstoreを更新している
        // マップ起動時にwatch内のprivatePlotsが検知されずにgetSource()が取得できない可能性があるため
        const privatePlots = await FireStoreService.getPrivatePlots();
        privatePlots.forEach((privatePlot) => {
          this.fetchPrivatePlots(privatePlot);
        });
      }

      this.$vs.loading.close("#movement-map > .con-vs-loading");
    },
    mapMoveStart() {
      this.mapEvent = "mapMoveStart";
      // this.unsubscribeFireStore();
    },
    mapMoveEnd() {
      this.$store.dispatch("enterpriseMapSetting/fetchCenterForGrid", {
        mapId: this.mapId,
        center: this.map.getCenter(),
      });
      this.updateMap(this);
      // 追跡設定を破棄する
      this.UPDATE_LAST_TRACKING_POSITION({
        mapId: "",
        center: [],
      });
    },
    mapZoomStart() {
      this.mapEvent = "mapZoomStart";
      // this.unsubscribeFireStore();
    },
    async mapZoomEnd() {
      this.mapEvent = "mapZoomEnd";
      this.$store.dispatch("enterpriseMapSetting/fetchZoomForGrid", {
        mapId: this.mapId,
        zoom: this.map.getZoom(),
      });
      this.updateMap(this);
      // 追跡設定を破棄する
      this.UPDATE_LAST_TRACKING_POSITION({
        mapId: "",
        center: [],
      });
      // ズームの最終値を更新する
      this.UPDATE_GRID_MAP_SETTINGS({
        mapId: this.mapId,
        last_zoom: this.map.getZoom(),
      });
      await FireStoreService.setGridMapSettings(this.mapId, {
        last_zoom: this.map.getZoom(),
      });
    },
    mapPitch() {
      this.mapEvent = "mapPitch";
    },
    // TODO: 現状使用しないためコメントアウト
    // unsubscribeFireStore() {
    //   this.$vs.loading.close("#map-loading > .con-vs-loading");
    //   if (this.updateVesselPositionsIntervalId != null) {
    //     clearInterval(this.updateVesselPositionsIntervalId);
    //     this.updateVesselPositionsIntervalId = null;
    //   }
    // },
    // NOTE: 普通に関数を定義すると同じコンポーネント間で関数を共有してしまい、コンポーネント間でdebounceして関数実行を間引いてしまう
    // なので、createdの中でupdateMapにロジックを紐づけて表示しているコンポーネントごとに固有のupdateMapを作成する
    // 処理の中身はupdateMapCreatorで作成する
    updateMap() {},
    // コンポーネントごとにupdateMap関数を作成
    updateMapCreator: () => {
      return _.debounce((self) => {
        if (self.map) {
          self.loadPlots(self.map);
          // QuadKeyArea更新
          if (self.fetchSearchQuadKeyArea()) {
            // unsubscribe
            // self.unsubscribeFireStore();
          }
          // FireStore監視
          self.setFeatureCollectionsFromApiWithInterval();
        }
      }, 1500);
    },
    async setupMapSources() {
      this.map.addSource(MapSource.VesselPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.VesselVectorSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.VesselPolygonSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      /*=========================================================================================
         自社船 MapSource
        ==========================================================================================*/
      this.map.addSource(MapSource.OwnVesselPolygonSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.OwnVesselPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.OwnVesselMarkerSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      /*=========================================================================================
         選択船 MapSource
        ==========================================================================================*/
      this.map.addSource(MapSource.SelectedVesselPolygonSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.SelectedVesselPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.SelectedVesselMarkerSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      /*=========================================================================================
         アンカー船 MapSource
        ==========================================================================================*/
      this.map.addSource(MapSource.AnchorVesselPolygonSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.AnchorVesselPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(MapSource.AnchorVesselMarkerSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      /*=========================================================================================
         アンカー Anchor
       ==========================================================================================*/
      this.map.addSource(MapSource.AnchorPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
      this.map.addSource(MapSource.AnchorToVesselLineSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      /*=========================================================================================
        パブリックプロット MapSource
       ==========================================================================================*/
      Object.values(PlotMapSource).forEach((source) => {
        this.map.addSource(source.id, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });
      });
      /*=========================================================================================
        プライベートプロット MapSource
       ==========================================================================================*/
      Object.values(PrivatePlotMapSource).forEach((source) => {
        this.map.addSource(source.id, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });
      });
      /*=========================================================================================
        航路標識種別 MapSource
      ==========================================================================================*/
      this.map.addSource(this.navigationalAidsSourceId, {
        type: "vector",
        url: "mapbox://aidea.navigational_aids?fresh=true",
      });

      /*=========================================================================================
        拠点 MapSource
      ==========================================================================================*/
      this.map.addSource(MapSource.BaseStationSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    },

    animation() {
      if (this.map.getSource(MapSource.OwnVesselPointSource.id)) {
        // アイコンが属するLayerのどれかをrepaintしてあげると他も追随してrepaintするようなので自社船のみ更新してみる
        this.map.setLayoutProperty(
          MapSource.OwnVesselPointSource.id,
          "icon-image",
          "pulsing",
        );
      }
      requestAnimationFrame(this.animation);
    },

    addAnimationIcon() {
      //アニメーションアイコン表示
      this.map.addImage("pulsing", this.pulsing, { pixelRatio: 2 });

      //選択船
      this.map.addLayer({
        id: MapSource.SelectedVesselPointSource.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        maxzoom: 20,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "pulsing",
        },
      });

      //自社船
      this.map.addLayer({
        id: MapSource.OwnVesselPointSource.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        maxzoom: 20,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "pulsing",
        },
      });

      requestAnimationFrame(this.animation);
    },

    /**
     * FireStore SnapShot範囲を生成
     *
     */
    fetchSearchQuadKeyArea() {
      let zoom = Math.floor(this.map.getZoom());
      zoom = zoom < 9 ? 9 : zoom;

      const mapCenter = this.map.getCenter();
      let tile = TileBelt.pointToTile(mapCenter.lng, mapCenter.lat, zoom);
      let tile2 = TileBelt.pointToTile(mapCenter.lng, mapCenter.lat, zoom + 1);
      let quadKey = TileBelt.tileToQuadkey(tile);
      //let childQuadKey = TileBelt.tileToQuadkey(tile2);
      let quadKeyPosition = TileBelt.tileToQuadkey(tile2) % 10;

      if (quadKey == this.cache.quadKey) {
        return false;
      }
      // reset
      this.cache.searchQuadKeys = [];

      let center = quadKey;
      switch (quadKeyPosition) {
        case 0: {
          let up = QuadKeyTools.sibling(center, "up");
          let upLeft = QuadKeyTools.sibling(up, "left");
          let left = QuadKeyTools.sibling(center, "left");
          this.cache.searchQuadKeys.push(up);
          this.cache.searchQuadKeys.push(upLeft);
          this.cache.searchQuadKeys.push(left);
          break;
        }
        case 1: {
          let right = QuadKeyTools.sibling(center, "right");
          let up = QuadKeyTools.sibling(center, "up");
          let upRight = QuadKeyTools.sibling(up, "right");
          this.cache.searchQuadKeys.push(right);
          this.cache.searchQuadKeys.push(up);
          this.cache.searchQuadKeys.push(upRight);
          break;
        }
        case 2: {
          let left = QuadKeyTools.sibling(center, "left");
          let down = QuadKeyTools.sibling(center, "down");
          let downLeft = QuadKeyTools.sibling(down, "left");
          this.cache.searchQuadKeys.push(left);
          this.cache.searchQuadKeys.push(down);
          this.cache.searchQuadKeys.push(downLeft);
          break;
        }
        case 3: {
          let right = QuadKeyTools.sibling(center, "right");
          let down = QuadKeyTools.sibling(center, "down");
          let downRight = QuadKeyTools.sibling(down, "right");
          this.cache.searchQuadKeys.push(right);
          this.cache.searchQuadKeys.push(down);
          this.cache.searchQuadKeys.push(downRight);
          break;
        }
        default: {
          break;
        }
      }

      this.cache.searchQuadKeys.push(center);
      this.cache.quadKey = quadKey;

      return true;
    },

    async loadPlots(map) {
      Vue.$log.debug(
        map.getContainer().id,
        `loadPlots has started`,
        this.$route.name,
      );
      let zoom = map.getZoom();
      if (zoom > 8) {
        let bbox = new Mapbox.LngLatBounds(
          map.getBounds().getSouthWest(),
          map.getBounds().getNorthEast(),
        );
        let poly = turf.bboxPolygon(bbox.toArray().flat());
        let factor = 1;

        if (zoom <= 10) {
          factor = 1;
        } else {
          // 拡大の場合は、画面外の領域も読み込み
          factor = zoom;
        }
        let scaledPoly = turf.transformScale(poly, factor);
        let northEast = scaledPoly.geometry.coordinates[0][2];
        let southWest = scaledPoly.geometry.coordinates[0][0];

        const features = await this.$store.dispatch(
          "enterprisePlot/searchFeaturesForPlot",
          {
            plotChildCategoryIds: "",
            northEastLatitude: northEast[1],
            northEastLongitude: northEast[0],
            southWestLatitude: southWest[1],
            southWestLongitude: southWest[0],
          },
        );
        /** パブリックプロット */
        const plotPointSource = this.map.getSource(
          PlotMapSource.PlotPointSource.id,
        );
        if (plotPointSource != null) {
          plotPointSource.setData({
            type: "FeatureCollection",
            features: features.filter((f) => f.geometry.type === "Point"),
          });
        }
        const plotPolygonSource = this.map.getSource(
          PlotMapSource.PlotPolygonSource.id,
        );
        if (plotPolygonSource != null) {
          plotPolygonSource.setData({
            type: "FeatureCollection",
            features: features.filter((f) => f.geometry.type === "Polygon"),
          });
        }
      }
    },

    // TODO: 現状何も行わないためコメントアウト
    // setSubscribes() {
    //   this.storeUnsubscribe = this.$store.subscribe((mutation) => {
    //     if (!this.map) return;
    //   });
    // },

    /**
     * 船舶位置情報群をタイミングをずらしながらAPI取得する.
     *
     * 以下の順番で非同期取得を開始.
     *
     * 1. map表示の中心座標を含む１辺20kmのタイルとその周辺＝合計９枚のタイルを取得開始.
     *      ---> 画面表示領域の真ん中あたりを先行して表示
     *
     * 2. 日本全域をカバーするタイルを取得開始. ( 暫定で Zoom:6 x 25枚のタイル に分割して並列取得)
     *      ---> 画面をZoom-outした時のために、あらかじめ周辺海域を含めた日本全域取得しておく
     *
     * 3. 画面に表示されている範囲をタイル分割して取得. 以降ポーリングで定期的に更新 (短周期).
     *      ---> 画面表示領域を常に更新 （Zoom値と画面解像度によるが、おおよそ 3x6 〜 5x7 枚のタイルを並列更新）
     *      ---> ユーザが Zoom変更(mapbox.zoomEnd) あるいは位置変更(mapbox.moveEnd)したら、画面表示領域の左上座標と右下座標を再計算＋ポーリング再実行
     *
     * 4. 日本全域をカバーする25枚のタイルをポーリングで定期的に更新 (長周期).
     *      ---> 日本全域の船舶位置が古くならない程度に最低限の更新
     *
     */
    setFeatureCollectionsFromApiWithInterval() {
      const japanAreaStartDelaySecond = 3; // 日本全体の取得の遅延開始時期(秒) おおむね表示範囲のFeatureCollectionを取得し終わった頃にスタート
      const japanAreaPollingSecond = 600; // 日本全体のポーリング間隔(秒)
      const nearAreaTileDelaySecond = 0.2; // 各タイルを並列取得する時の多重度を抑えるための時間差指定（画面表示領域）
      const japanAreaTileDelaySecond = 0.3; // 各タイルを並列取得する時の多重度を抑えるための時間差指定（日本全体）
      const cleanerPollingSecond = 60; // 指定時間以上経過している船舶は非表示にする処理のポーリング間隔(秒)

      const mapZoom = this.map.getZoom();
      const nearAreaPollingSecond = // 画面表示領域のポーリング間隔(秒)
        mapZoom >= 14
          ? 3 // zoom = 14 => scaleUnit = 200m ポリゴン表示開始サイズ
          : mapZoom >= 10
          ? 10 // zoom = 10 => scaleUnit = 3km 通常（初期表示サイズ）
          : mapZoom >= 9
          ? 3 * 60 // zoom =  9 => scaleUnit = 5km 船舶は○アイコン（東京湾全域程度）
          : mapZoom >= 5
          ? 10 * 60 // zoom =  5 => scaleUnit = 100km ほぼ日本全域
          : 10 * 60;

      if (this.intervalIdForGetFeatureCollection.length > 0) {
        // 短周期のインターバルがあればリセット（画面位置やZoom値が変わっているため）
        this.clearShortPolling([...this.intervalIdForGetFeatureCollection]);
        this.intervalIdForGetFeatureCollection.splice(0);
      }

      let tileZoom = 0;
      let tileDistanceFromCenter = 0;
      let tileList = [];

      const mapCenter = this.map.getCenter();
      // 初回： mapboxに表示された中心座標とそのzoom値に応じた9枚のパネルを取得. // １辺が約20km zoom=11 x 9枚で東京湾周辺が埋まる
      tileZoom = Math.floor(this.map.getZoom() > 11 ? 11 : this.map.getZoom()); // zoom-out を考慮して、ある程度zoomしたら、それより狭い範囲を取得しない
      tileDistanceFromCenter = 1;
      tileList = this.getTileListSquare(
        tileZoom,
        tileDistanceFromCenter,
        mapCenter,
      );
      this.setFeatureCollectionsFromApi(tileList, 0, null, "init-near");

      // ブラウザ枠に含まれるタイルをポーリング
      const intervalId = setInterval(() => {
        if (!this.checkPollingValidation("poll-near")) {
          return;
        }

        const currentZoom = Math.floor(this.map.getZoom());
        tileZoom = currentZoom > 10 ? 10 : currentZoom < 8 ? 8 : currentZoom;
        tileList = this.getTileListRectangle(
          tileZoom,
          this.map.getBounds().getNorthWest(),
          this.map.getBounds().getSouthEast(),
        );
        this.setFeatureCollectionsFromApi(
          tileList,
          nearAreaTileDelaySecond,
          null,
          "poll-near",
        );
      }, nearAreaPollingSecond * 1000);
      this.intervalIdForGetFeatureCollection.push({
        intervalId: intervalId,
        momentValue: moment().valueOf(),
      });

      if (!this.isReadyGetFeatureCollectionLong) {
        this.isReadyGetFeatureCollectionLong = true;

        // 日本全域を取得開始 （広域取得はページ初回表示時にポーリング開始したらページ消滅まで継続）
        setTimeout(() => {
          // 日本全域 : 初回
          tileZoom = 6;
          tileDistanceFromCenter = 2;
          const japanCenterLngLat = {
            lng: MAP_DEFAULT_POSITION.center[0],
            lat: MAP_DEFAULT_POSITION.center[1],
          };
          tileList = this.getTileListSquare(
            tileZoom,
            tileDistanceFromCenter,
            japanCenterLngLat,
          );
          this.setFeatureCollectionsFromApi(
            tileList,
            japanAreaTileDelaySecond,
            tileZoom,
            "init-japan",
          );

          // 日本全域 : ポーリング
          this.intervalIdForGetFeatureCollectionLong.push(
            setInterval(() => {
              if (!this.checkPollingValidation("poll-japan")) {
                return;
              }

              tileZoom = 8;
              tileDistanceFromCenter = 12;
              this.setFeatureCollectionsFromApi(
                tileList,
                japanAreaTileDelaySecond,
                tileZoom,
                "poll-japan",
              );
            }, japanAreaPollingSecond * 1000),
          );
        }, japanAreaStartDelaySecond * 1000);

        // 指定時間以上経過している船舶は非表示とするため、定期的にクリーナーを実行。
        this.intervalIdForGetFeatureCollectionLong.push(
          setInterval(() => {
            if (!this.checkPollingValidation("cleaner")) {
              return;
            }

            const ownVesselIds = this.ownVessels.map((x) => x.id);
            const anchorVesselIds = this.getAnchorVesselIds;
            const tenMinutesAgo = moment().subtract(10, "minutes").valueOf();
            const ownVesselDisplayDurationAgo = this
              .ownVesselDisplayDurationTime
              ? moment()
                  .subtract(this.ownVesselDisplayDurationTime, "minutes")
                  .valueOf()
              : 0;

            Array.from(this.featurePositionMap).forEach(
              ([vesselId, featureObj]) => {
                // 自社船： "自社船舶表示期限"の設定値以上経過している場合は表示しない。
                // 自社船以外の船：10分以上経過している場合は表示しない。
                // アンカー船は常に表示するため除外する
                if (anchorVesselIds.includes(vesselId)) return;
                if (
                  (ownVesselIds.includes(vesselId) &&
                    featureObj.properties.t < ownVesselDisplayDurationAgo) ||
                  (!ownVesselIds.includes(vesselId) &&
                    featureObj.properties.t < tenMinutesAgo)
                ) {
                  this.featurePositionMap.delete(vesselId);
                  this.featureVectorMap.delete(vesselId);
                  this.featurePolygonMap.delete(vesselId);
                }
              },
            );

            // メモリリーク対策。  頻繁に更新されるfeature***Mapのオブジェクトポインタを差し替えて、ガベージコレクションを促す。
            const featurePositionMapBackup = _.cloneDeep(
              this.featurePositionMap,
            );
            const featureVectorMapBackup = _.cloneDeep(this.featureVectorMap);
            const featurePolygonMapBackup = _.cloneDeep(this.featurePolygonMap);
            this.featurePositionMap = null;
            this.featureVectorMap = null;
            this.featurePolygonMap = null;
            this.featurePositionMap = featurePositionMapBackup;
            this.featureVectorMap = featureVectorMapBackup;
            this.featurePolygonMap = featurePolygonMapBackup;

            // vessel_geometryAPI取得に使っている setTimeout のタイマーIDも、古すぎるものはクリーニング。
            this.timeoutIdForGetFeatureCollection =
              this.timeoutIdForGetFeatureCollection.filter(
                (timeout) => timeout.t > tenMinutesAgo,
              );
          }, cleanerPollingSecond * 1000),
        );
      }

      // 参考数値：
      // zoom=11 x 9枚で東京湾周辺が埋まる（zoom=11 の場合、 日本緯度では１辺が約20km）
      // zoom=6 x 25枚で日本が埋まる。 全世界は zoom=6 x 64 x 64 でカバー ( 2^6 = 64 )
    },

    checkPollingValidation(keyName) {
      if (this.$route.name !== "enterprise-map-view") {
        // beforeDestroy() で削除しきれなかったポーリング処理を検知したら、終了処理をコール。
        Vue.$log.debug(
          `remained polling process has detected. So, destroy. (${keyName}) this.$route.name = `,
          this.$route.name,
        );
        this.destroyPolling();
        return false;
      }
      return true;
    },
    clearShortPolling(intervalList) {
      this.intervalHistoryList.push(intervalList);
      this.intervalHistoryList.forEach((intervalSet) => {
        intervalSet.forEach((intervalObj) =>
          clearInterval(intervalObj.intervalId),
        );
      });
    },
    destroyPolling() {
      this.webWorker.terminate();
      this.clearShortPolling([...this.intervalIdForGetFeatureCollection]);
      this.intervalIdForGetFeatureCollection = [];
      this.intervalIdForGetFeatureCollectionLong.forEach((interval) =>
        clearInterval(interval),
      );
      this.intervalIdForGetFeatureCollectionLong = [];
      this.timeoutIdForGetFeatureCollection.forEach((timeout) =>
        clearTimeout(timeout.timeoutId),
      );
      this.timeoutIdForGetFeatureCollection = [];
    },

    /**
     * 表示されたmapの左上緯度経度から右下緯度経度に含まれるタイル番号を算出する.
     */
    getTileListRectangle(tileZoom, northWestLngLat, southEastLngLat) {
      let northWestTile = TileBelt.pointToTile(
        northWestLngLat.lng,
        northWestLngLat.lat,
        tileZoom,
      );
      let souchEastTile = TileBelt.pointToTile(
        southEastLngLat.lng,
        southEastLngLat.lat,
        tileZoom,
      );

      const leftTileNumber = northWestTile[0] - 1;
      const rightTileNumber = souchEastTile[0] + 1;
      const topTileNumber = northWestTile[1] - 1;
      const bottomTileNumber = souchEastTile[1] + 1;

      return this.getTileList(
        tileZoom,
        topTileNumber,
        rightTileNumber,
        bottomTileNumber,
        leftTileNumber,
      );
    },

    /**
     * 表示されたmapの中心緯度経度とその周辺のタイル番号を算出する.
     */
    getTileListSquare(tileZoom, tileDistanceFromCenter, centerLngLat) {
      // tileDistanceFromCenter(normal):  0=1x1=1枚, 1=3x3=9枚, 2=5x5=25枚,

      // 地図の中央を含むタイル
      let centerTile = TileBelt.pointToTile(
        centerLngLat.lng,
        centerLngLat.lat,
        tileZoom,
      );

      const leftTileNumber = centerTile[0] - tileDistanceFromCenter;
      const rightTileNumber = centerTile[0] + tileDistanceFromCenter;
      const topTileNumber = centerTile[1] - tileDistanceFromCenter;
      const bottomTileNumber = centerTile[1] + tileDistanceFromCenter;

      const tileList = this.getTileList(
        tileZoom,
        topTileNumber,
        rightTileNumber,
        bottomTileNumber,
        leftTileNumber,
      );

      // 中心から渦巻き状に展開するように並べ替え
      const stepDirectionList = [
        [0, 1], // 下移動
        [-1, 0], // 左移動
        [0, -1], // 上移動
        [1, 0], // 右移動
      ];
      const centerX = Math.floor((leftTileNumber + rightTileNumber) / 2);
      const centerY = Math.floor((topTileNumber + bottomTileNumber) / 2);

      let tileListWork = Array.from(tileList);
      let stepIndex = 0;
      let tileListSpiral = [];
      let i = 0;

      let workX = centerX;
      let workY = centerY;
      let findIndex = tileListWork.findIndex(
        (tile) => tile[0] === workX && tile[1] === workY,
      );
      tileListSpiral.push(tileListWork[findIndex]);
      tileListWork.splice(findIndex, 1);

      while (tileListWork.length > 0 || i < 200) {
        let findIndex = tileListWork.findIndex(
          (tile) =>
            tile[0] === workX + stepDirectionList[stepIndex][0] &&
            tile[1] === workY + stepDirectionList[stepIndex][1],
        );
        if (findIndex >= 0) {
          tileListSpiral.push(tileListWork[findIndex]);
          tileListWork.splice(findIndex, 1);

          workX = workX + stepDirectionList[stepIndex][0];
          workY = workY + stepDirectionList[stepIndex][1];
          stepIndex = (stepIndex + 1) % stepDirectionList.length;
        } else {
          stepIndex =
            (stepIndex + stepDirectionList.length - 1) %
            stepDirectionList.length;
        }

        i = i + 1;
      }
      return tileListSpiral;
    },

    /**
     *  引数の指定範囲に従ったタイルリストを取得.
     */
    getTileList(
      tileZoom,
      topTileNumber,
      rightTileNumber,
      bottomTileNumber,
      leftTileNumber,
    ) {
      let tileList = [];
      for (let ry = topTileNumber; ry <= bottomTileNumber; ry++) {
        for (let rx = leftTileNumber; rx <= rightTileNumber; rx++) {
          tileList.push([rx, ry, tileZoom]);
        }
      }
      return tileList;
    },

    /**
     * 引数のタイルリスト毎にAPIをコールし、取得結果を既存のFeatureCollectionにマージして 該当のMapSourceにセット.
     *
     */
    setFeatureCollectionsFromApi(tileList, delaySecond, zoom, purpose) {
      Vue.$log.debug(
        moment().format("HH:mm:ss"),
        this.mapId,
        `setFeatureCollectionsFromApi() purpose = ${purpose}. tileList.length = ${tileList.length}`,
      );
      // zoomが4未満の場合はAPIを発行しない
      if (this.map.getZoom() < 4) {
        return;
      }
      // tile[2]（tileのzoom値）が0のときにquadkeyが空文字になるため除外する
      const newTileList = tileList.filter((tile) => tile[2] > 0);

      newTileList.forEach((tile, index) => {
        const timeoutId = setTimeout(async() => {
          try {
            const originalQuadKey = TileBelt.tileToQuadkey(tile); // そのタイルのquadKey番号
            const nextQuadKey = (parseInt(originalQuadKey, 4) + 1)
              .toString(4) // そのタイルの次のquadKey番号 (4進数計算)
              .padStart(originalQuadKey.length, "0"); // 0パディング

            let northWestQuadKey = originalQuadKey;
            let southEastQuadKey = nextQuadKey;

            const payload = {
              north_west_quad_key: northWestQuadKey,
              south_east_quad_key: southEastQuadKey,
              own_vessel_display_duration_minutes:
                this.ownVesselDisplayDurationTime,
              enterprise_vessel_ids: this.ownVessels.map((x) => x.id).join(","),
              selected_vessel_ids: this.selectedVessels
                .map((x) => x.id)
                .join(","),
              zoom: Math.floor(zoom || this.map.getZoom()),
            };

            await AuthService.getIdToken().then((idToken) => {
              // webWorkerで vessel_geometry取得APIをコール
              this.webWorker.postMessage({
                payload: payload,
                idToken: idToken,
              });
            });
          } catch (e) {
            Vue.$log.debug("setFeatureCollection failed. ", e);
          }
        }, delaySecond * index * 1000); // 引数のtileListの順番で処理できるように少しだけ時間をずらして取得.

        this.timeoutIdForGetFeatureCollection.push({
          timeoutId: timeoutId,
          t: moment().valueOf(),
        });
      });
    },

    async setVesselGeometryToMapSources(receivedData) {
      if (this.getAddonFlag("GKMG_ANCHOR_MANAGEMENT")) {
        await this.setAnchorVesselFeatureToMapBySource(
          receivedData.featureMapBySource,
          this,
        );
      }
      const featureMapBySource = receivedData.featureMapBySource;
      if (featureMapBySource && featureMapBySource.positions) {
        // featurePositionMapは LiveMap.vueのローカル変数。 e[0] は vessel_id. 頻繁な更新なのでmutationによる値保存は実施しない。
        Object.entries(featureMapBySource.positions).forEach(
          ([vesselId, featureObj]) => {
            this.featurePositionMap.set(vesselId, featureObj);
          },
        );
        Object.entries(featureMapBySource.vectors).forEach(
          ([vesselId, featureObj]) => {
            this.featureVectorMap.set(vesselId, featureObj);
          },
        );
        Object.entries(featureMapBySource.polygons).forEach(
          ([vesselId, featureObj]) => {
            this.featurePolygonMap.set(vesselId, featureObj);
          },
        );

        // mapboxへ setData
        this.setDataToMap(
          this.map,
          this.featurePositionMap,
          this.featureVectorMap,
          this.featurePolygonMap,
          this.ownVessels,
          this.selectedVessels,
          this.anchors,
          this.equipmentBaseStations,
          this.getAnchorVesselIds,
        );
      }
    },
    // NOTE: updateMapと同じく、普通に関数を定義するとコンポーネント間で関数実行を間引いてしまう
    // createdの中でロジックを紐づけて表示しているコンポーネントごとに固有のsetDataToMapを作成する
    setDataToMap() {},
    // コンポーネントごとにsetDataToMap関数を作成
    setDataToMapCreator() {
      return _.throttle(
        (
          map,
          featurePositionMap,
          featureVectorMap,
          featurePolygonMap,
          ownVessels,
          selectedVessels,
          anchors,
          equipmentBaseStations,
          anchorVesselIds,
        ) => {
          Vue.$log.debug(
            map.getContainer().id,
            `setDataToMap has started`,
            moment().format("HH:mm:ss"),
          );
          const ownVesselIds = ownVessels.map((x) => x.id);
          const selectedVesselIds = selectedVessels.map((x) => x.id);
          const positionFeatureList = Array.from(featurePositionMap.values());
          const vectorFeatureList = Array.from(featureVectorMap.values());
          const polygonFeatureList = Array.from(featurePolygonMap.values());

          // 他社船 position
          let otherVesselPointFeatureCollection = {
            type: "FeatureCollection",
            features: positionFeatureList
              .filter((x) => x != null)
              .filter((x) => !ownVesselIds.includes(x.properties.id))
              // アンカー船は取り除く
              .filter((x) => !anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.VesselPointSource.id)
            .setData(otherVesselPointFeatureCollection);

          // 選択船 position
          let selectedVesselPointFeatureCollection = {
            type: "FeatureCollection",
            features: positionFeatureList
              .filter((x) => x != null)
              .filter((x) => selectedVesselIds.includes(x.properties.id))
              // アンカー船は取り除く
              .filter((x) => !anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.SelectedVesselPointSource.id)
            .setData(selectedVesselPointFeatureCollection);
          map
            .getSource(MapSource.SelectedVesselMarkerSource.id)
            .setData(selectedVesselPointFeatureCollection);

          // 自社船 position
          let ownVesselPointFeatureCollection = {
            type: "FeatureCollection",
            features: positionFeatureList
              .filter((x) => x != null)
              .filter((x) => ownVesselIds.includes(x.properties.id))
              // アンカー船は取り除く
              .filter((x) => !anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.OwnVesselPointSource.id)
            .setData(ownVesselPointFeatureCollection);
          map
            .getSource(MapSource.OwnVesselMarkerSource.id)
            .setData(ownVesselPointFeatureCollection);

          // アンカー船 position
          let anchorVesselPointFeatureCollection = {
            type: "FeatureCollection",
            features: positionFeatureList
              .filter((x) => x != null)
              // アンカー船
              .filter((x) => anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.AnchorVesselPointSource.id)
            .setData(anchorVesselPointFeatureCollection);
          map
            .getSource(MapSource.AnchorVesselMarkerSource.id)
            .setData(anchorVesselPointFeatureCollection);

          // ベクトル表示
          let vesselFeatures = vectorFeatureList.map((x) => {
            return {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: [
                  [
                    x.geometry.coordinates[0].coordinates[0],
                    x.geometry.coordinates[0].coordinates[1],
                  ],
                  [
                    x.geometry.coordinates[1].coordinates[0],
                    x.geometry.coordinates[1].coordinates[1],
                  ],
                ],
              },
              properties: x.properties,
            };
          });
          let vesselVectorFeatureCollection = {
            type: "FeatureCollection",
            features: vesselFeatures,
          };
          map
            .getSource(MapSource.VesselVectorSource.id)
            .setData(vesselVectorFeatureCollection);

          // ポリゴン表示
          const polygonFeaturesWork = polygonFeatureList.flat();
          const polygonFeatures = polygonFeaturesWork.map((x) => {
            return {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: [
                  x.geometry.coordinates[0].map((x1) => x1.coordinates),
                ],
              },
              properties: x.properties,
            };
          });

          // ポリゴン (他社船)
          let otherVesselPolygonFeatureCollection = {
            type: "FeatureCollection",
            features: polygonFeatures
              .filter((x) => !ownVesselIds.includes(x.properties.id))
              // アンカー船を取り除く
              .filter((x) => !anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.VesselPolygonSource.id)
            .setData(otherVesselPolygonFeatureCollection);

          // ポリゴン (選択船)
          let selectedVesselPolygonFeatureCollection = {
            type: "FeatureCollection",
            features: polygonFeatures
              .filter((x) => selectedVesselIds.includes(x.properties.id))
              // アンカー船を取り除く
              .filter((x) => !anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.SelectedVesselPolygonSource.id)
            .setData(selectedVesselPolygonFeatureCollection);

          // ポリゴン表示(自社船)
          let ownVesselPolygonFeatureCollection = {
            type: "FeatureCollection",
            features: polygonFeatures
              .filter((x) => ownVesselIds.includes(x.properties.id))
              // アンカー船を取り除く
              .filter((x) => !anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.OwnVesselPolygonSource.id)
            .setData(ownVesselPolygonFeatureCollection);

          // ポリゴン表示(アンカー船)
          let anchorVesselPolygonFeatureCollection = {
            type: "FeatureCollection",
            features: polygonFeatures
              // アンカー船
              .filter((x) => anchorVesselIds.includes(x.properties.id)),
          };
          map
            .getSource(MapSource.AnchorVesselPolygonSource.id)
            .setData(anchorVesselPolygonFeatureCollection);

          // 自社船・選択船に紐づくアンカー
          // アンカーの中心位置を計算
          // 船本体のポリゴンがある場合はその0番目、ない場合はpointの緯度経度を中心計算に使用する
          const anchorVesselFeaturesForCalcCenter = [
            ...anchorVesselPolygonFeatureCollection.features,
            ...anchorVesselPointFeatureCollection.features,
          ].reduce((prev, feature) => {
            const vesselId = feature.properties && feature.properties.id;
            if (!Object.prototype.hasOwnProperty.call(prev, vesselId)) {
              prev[vesselId] = feature;
            }
            return prev;
          }, {});

          const anchorVesselCenterPoints = Object.keys(
            anchorVesselFeaturesForCalcCenter,
          ).reduce((prev, vesselId) => {
            const feature = anchorVesselFeaturesForCalcCenter[vesselId];
            if (Object.prototype.hasOwnProperty.call(prev, vesselId)) {
              return prev;
            }
            if (feature.geometry.type === "Polygon") {
              const collections = feature.geometry.coordinates[0].map((c) => {
                return turf.point(c);
              });
              const features = turf.featureCollection(collections);
              const centerFeature = turf.center(features);
              prev[vesselId] = centerFeature.geometry.coordinates;
            } else {
              prev[vesselId] = feature.geometry.coordinates;
            }
            return prev;
          }, {});

          /** アンカーのポイント */
          let anchorPointCollection = {
            type: "FeatureCollection",
            features: anchors.reduce((prev, anchor) => {
              const anchorVesselId = anchor.vessel_id;
              const feature = anchorVesselFeaturesForCalcCenter[anchorVesselId];
              const center = anchorVesselCenterPoints[anchorVesselId];
              anchor.points.forEach((point) => {
                let rotation = 0; // 回転角
                let distance = 0; // 距離
                let properties = {};
                if (feature && center) {
                  // 回転角を求める
                  rotation = turf.bearing(
                    [point.longitude, point.latitude],
                    [center[0], center[1]],
                  );
                  // 船舶とアンカーの距離を求める
                  distance = turf.distance(
                    [point.longitude, point.latitude],
                    [center[0], center[1]],
                    { units: "kilometers" },
                  );
                  properties = feature.properties;
                }
                prev.push({
                  geometry: {
                    coordinates: [point.longitude, point.latitude],
                    type: "Point",
                  },
                  properties: Object.assign({}, properties, anchor, {
                    r: rotation,
                    // 小数点第1位切り上げして単位をメートルに変換
                    d:
                      distance > 0
                        ? Math.ceil(distance * 1000 * 10) / 10
                        : "---",
                  }),
                  type: "Feature",
                });
              });
              return prev;
            }, []),
          };
          map
            .getSource(MapSource.AnchorPointSource.id)
            .setData(anchorPointCollection);

          /** アンカーと船舶を結ぶ線 */
          let anchorToVesselLineCollection = {
            type: "FeatureCollection",
            features: anchors.reduce((prev, anchor) => {
              const anchorVesselId = anchor.vessel_id;
              const feature = anchorVesselFeaturesForCalcCenter[anchorVesselId];
              const center = anchorVesselCenterPoints[anchorVesselId];
              // 船舶・中心座標が取れ、アンカーが存在する場合のみラインを描画する
              if (feature && center && anchor.points.length > 0) {
                anchor.points.forEach((point) => {
                  prev.push({
                    geometry: {
                      coordinates: [
                        [center[0], center[1]],
                        [point.longitude, point.latitude],
                      ],
                      type: "LineString",
                    },
                    properties: Object.assign({}, feature.properties, anchor),
                    type: "Feature",
                  });
                });
              }
              return prev;
            }, []),
          };
          map
            .getSource(MapSource.AnchorToVesselLineSource.id)
            .setData(anchorToVesselLineCollection);

          // 拠点の座標
          if (this.getAddonFlag("GKMG_BASE_STATION")) {
            let equipmentBaseStationFeatures = equipmentBaseStations.map(
              (b) => {
                return {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: [b.point.longitude, b.point.latitude],
                  },
                  properties: {},
                };
              },
            );
            let equipmentBaseStationFeatureCollection = {
              type: "FeatureCollection",
              features: equipmentBaseStationFeatures,
            };
            map
              .getSource(MapSource.BaseStationSource.id)
              .setData(equipmentBaseStationFeatureCollection);
          }
        },
        800,
      );
    },

    cratePulsing(_map) {
      let size = 150;
      this.pulsing = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function () {
          let canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d");
        },

        render: function () {
          let duration = 2000;
          let t = (performance.now() % duration) / duration;

          let radius = (size / 2) * 0.3;
          let outerRadius = (size / 2) * 0.7 * t + radius;
          let context = this.context;

          context.clearRect(0, 0, this.width, this.height);
          context.beginPath();
          context.arc(
            this.width / 2,
            this.height / 2,
            outerRadius,
            0,
            Math.PI * 2,
          );
          context.fillStyle = "rgba(255, 255, 255," + (1 - t) + ")";
          context.fill();

          this.data = context.getImageData(0, 0, this.width, this.height).data;

          return true;
        },
      };
    },

    /**
     * 初回起動時にFireStoreのtracking_targetが存在する場合はその座標を中心に設定
     * */
    async setTrackingTargetCenter(settings) {
      const trackingTarget = settings.tracking_target;
      let targetPositionCenter = null;
      if (trackingTarget.id && trackingTarget.type) {
        switch (trackingTarget.type) {
          case "vessel":
            targetPositionCenter = await this.getVesselCoordinate(
              trackingTarget.id,
            );
            break;
          case "plot":
            targetPositionCenter = await this.getPlotCoordinate(
              trackingTarget.id,
            );
            break;
          case "privatePlot":
            targetPositionCenter = this.getAddonFlag("GKMG_PRIVATE_PLOT")
              ? await this.getPrivatePlotCoordinate(trackingTarget.id)
              : null;
            break;
          case "base":
            targetPositionCenter = this.getAddonFlag("GKMG_BASE_STATION")
              ? await this.getBaseCoordinate(trackingTarget.id)
              : null;
            break;
        }
      }
      if (!targetPositionCenter) {
        targetPositionCenter = settings.default_position.center;
      }
      return targetPositionCenter;
    },
    // 船舶の座標取得
    async getVesselCoordinate(vesselId) {
      const response = await this.getLastPositionWithTimeout(vesselId);
      if (
        response &&
        response.longitude != undefined &&
        response.latitude != undefined
      ) {
        return [response.longitude, response.latitude];
      } else {
        return null;
      }
    },
    // プロットの座標取得
    async getPlotCoordinate(plotId) {
      const request = {
        start: 0,
        limit: 99999,
      };
      const response = await this.$store.dispatch(
        "enterprisePlot/fetchPlots",
        request,
      );
      if (!response) return null;
      const plot = response.plots.find((plot) => {
        return plot.id == plotId;
      });
      if (plot && plot.plot_points) {
        const collections = plot.plot_points.map((e) => {
          return turf.point([e.longitude, e.latitude]);
        });
        const features = turf.featureCollection(collections);
        const center = turf.center(features);
        return center.geometry.coordinates;
      } else {
        return null;
      }
    },
    // プライベートプロットの座標取得
    async getPrivatePlotCoordinate(plotHash) {
      // "T.MapViewGridMain"のcreated()処理が完了前にmounted()処理が発生しており、プロット情報（state）を配列として参照できない場合はFirestoreから直接参照する。
      const targetPlot =
        this.privatePlots.find((plot) => {
          return plot.hash === plotHash;
        }) || (await FireStoreService.getPrivatePlotByHash(plotHash));
      if (
        !targetPlot ||
        !targetPlot.center_point ||
        targetPlot.center_point.longitude == null ||
        targetPlot.center_point.latitude == null
      ) {
        return null;
      }
      return [
        targetPlot.center_point.longitude,
        targetPlot.center_point.latitude,
      ];
    },
    // 拠点の座標取得
    // TODO: 仕様変更によりポリゴンの中心座標に変わる可能性有
    async getBaseCoordinate(baseHash) {
      if (!baseHash) return null;
      // "T.MapViewGridMain"のcreated()処理が完了前にmounted()処理が発生しており、拠点情報（state）を配列として参照できない場合はFirestoreから直接参照する。
      const targetBase =
        this.equipmentBaseStations.find((base) => {
          return base.hash === baseHash;
        }) || (await FireStoreService.getEquipmentBaseStation(baseHash));
      if (
        !targetBase ||
        !targetBase.point ||
        targetBase.point.longitude == null ||
        targetBase.point.latitude == null
      ) {
        return null;
      }
      return [targetBase.point.longitude, targetBase.point.latitude];
    },
    // NOTE: updateMapと同じく、普通に関数を定義するとコンポーネント間で関数実行を間引いてしまう
    // createdの中でロジックを紐づけて表示しているコンポーネントごとに固有のsetAnchorVesselFeatureToMapBySourceを作成する

    setAnchorVesselFeatureToMapBySource() {},

    // WARNING: 副作用あり -> featureMapBySourceのプロパティを更新する
    setAnchorVesselFeatureToMapBySourceCreator() {
      return _.throttle(async (featureMapBySource, self) => {
        Vue.$log.debug(
          `start setAnchorVesselFeatureToMapBySource.`,
          self.$route.name,
          self.mapId, // NOTE: グリッドの場合にどのマップで呼ばれているか確認する
        );
        await Promise.allSettled(
          self.getAnchorVesselIds.map(async (anchorVesselId) => {
            // featureMapBySourceにanchorVesselIdがない場合は右メニュークリック時に取得した lastPosition のデータを画面描画に使用
            if (
              !featureMapBySource.positions[Number(anchorVesselId)] &&
              !self.featurePositionMap.has(anchorVesselId)
            ) {
              const vessel = self.getLastVesselPosition(anchorVesselId);
              // lastPositionからも取得できなかった船舶は右メニューに表示できない
              // [INFO] 対象の船舶がないとき(undefined or error_code=300 or status=500となる)
              if (
                !vessel ||
                vessel.error_code === 300 ||
                vessel.status === 500
              ) {
                return;
              }
              featureMapBySource.positions[Number(vessel.vessel_id)] = {
                geometry: {
                  coordinates: [vessel.longitude, vessel.latitude],
                  type: "Point",
                },
                properties: {
                  n: vessel.vessel_name_ja || vessel.vessel_name,
                  id: vessel.vessel_id,
                },
                type: "Feature",
              };
            }
          }),
        );
      }, 1000);
    },
  },
  async created() {
    this.updateMap = this.updateMapCreator(); // コンポーネントごとに固有のupdateMap関数を作成
    this.setDataToMap = this.setDataToMapCreator(); // コンポーネントごとに固有のsetDataToMap関数を作成
    this.setAnchorVesselFeatureToMapBySource =
      this.setAnchorVesselFeatureToMapBySourceCreator(); // コンポーネントごとに固有のsetDataToMap関数を作成
    const webWorker = this.getAddonFlag("GKMG_AIS_FOR_SIGNAL")
      ? new WebWorkerForFetchVesselGeometryForSignal()
      : new WebWorkerForFetchVesselGeometry();
    webWorker.addEventListener("message", async (e) => {
      await this.setVesselGeometryToMapSources(e.data);
    });
    this.webWorker = webWorker;
    this.isDebug = await LocalDbService.getIsDebug();
  },
  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.resetLiveMapState();
    this.$store.dispatch("enterprisePlot/resetPlotState");
    this.resetPrivatePlotState();
    this.resetAnchorState();
    this.resetVesselEquipmentBaseStationState();
    // this.storeUnsubscribe();
    // キャッシュクリア
    this.$store.dispatch("enterpriseMapVesselTarget/clear");

    this.destroyPolling();
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";
@import "src/assets/scss/vuexy/variables";

.map-view-grid-item-container {
  /* mapboxのロゴを非表示にする */
  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  .map-controls-overlay {
    position: absolute;
    // right: 20px;
    // bottom: 88px;
    width: 20px;
    height: 20px;
    z-index: 10;
    transition-delay: 0.3s;
    -webkit-transform: translate3d(0%, 0px, 0px);
    transform: translate3d(0%, 0px, 0px);
    visibility: visible;
    transition: all 0.4s ease 0s;
    -webkit-transition: all 0.4s ease 0s;

    &.is-not-open {
      -webkit-transform: translate3d(300px, 0px, 0px);
      transform: translate3d(300px, 0px, 0px);
      visibility: visible;
      transition: all 0.4s ease 0s;
      -webkit-transition: all 0.4s ease 0s;
    }
  }
  .expired-vessel-live-position-popup {
    max-width: 200px;

    .mapboxgl-popup-tip {
      border-top-color: #333333 !important;
      border-bottom-color: #333333 !important;
    }

    .mapboxgl-popup-content {
      background-color: #333333 !important;
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 21px;
      color: #f2f2f2;
    }

    .mapboxgl-popup-close-button {
      color: #f2f2f2 !important;
      font-size: 20px;
      padding-right: 4px;
    }
  }

  .base-station-label-popup {
    top: -20px;
    .mapboxgl-popup-tip {
      border-top-color: #f2f2f2 !important;
      border-bottom-color: #f2f2f2 !important;
    }

    .mapboxgl-popup-content {
      position: relative;
      background-color: #f2f2f2 !important;
      font-style: normal;
      font-weight: 300;
      font-size: 12px;
      line-height: 18px;
      color: $aisea-dark-blue;
      font-family: $aisea-font-family;
      .popup-text-content:not(:last-child) {
        margin-bottom: 4px;
      }
      .popup-title {
        font-weight: bold;
      }
      .mapboxgl-popup-close-button {
        position: absolute;
        color: #333333 !important;
        right: 0;
        top: 0;
        font-size: 22px;
        width: 22px;
        height: 22px;
      }
    }
  }
}
</style>
